import { Outlet, useLocation, useNavigate } from "react-router";
import { PageShell } from "../../components/layout/PageShell";
import { SegmentedTabs } from "../../components/SegmentedTabs";
import { LOG_INSIGHT_CONFIG } from "../../types/logReview";

/**
 * Shell for Change Assurance Governance.
 *
 * The module is one destination in the sidebar with three views behind it,
 * rather than three siblings: Overview is the dashboard, and the two Insights
 * queues are the workflow stages that act on what it surfaces. Listing them
 * separately implied they were unrelated pages.
 *
 * The tabs are ROUTED, not local state — each view is deep-linkable, the back
 * button steps between them, and a refresh keeps you where you were. The shell
 * owns the page title and strapline so the views below render only their body.
 */

export const CAG_BASE_PATH = "/change-assurance-governance";

interface CagTab {
  id: string;
  label: string;
  /** Path relative to CAG_BASE_PATH. Empty string is the index route. */
  segment: string;
  subtitle: string;
}

const TABS: CagTab[] = [
  {
    id: "overview",
    label: "Overview",
    segment: "",
    subtitle:
      "Monitor change-request log compliance and raise justifications for non-compliant findings.",
  },
  {
    id: "reviewer",
    label: "Log Reviewer Insights",
    segment: "reviewer-insights",
    subtitle: LOG_INSIGHT_CONFIG.reviewer.subtitle,
  },
  {
    id: "ciso",
    label: "CISO Insights",
    segment: "ciso-insights",
    subtitle: LOG_INSIGHT_CONFIG.approver.subtitle,
  },
];

const pathFor = (tab: CagTab) =>
  tab.segment ? `${CAG_BASE_PATH}/${tab.segment}` : CAG_BASE_PATH;

export const ChangeAssuranceGovernancePage = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Longest segment first, so "reviewer-insights" is not shadowed by the
  // index tab's empty segment (which every path under the base matches).
  const active =
    [...TABS]
      .sort((a, b) => b.segment.length - a.segment.length)
      .find((tab) => pathname === pathFor(tab) || pathname.startsWith(`${pathFor(tab)}/`)) ??
    TABS[0];

  return (
    <PageShell className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-foreground">
          Change Assurance Governance
        </h1>
        <p className="text-sm text-muted-foreground">{active.subtitle}</p>
      </div>

      <SegmentedTabs
        items={TABS.map((tab) => ({ id: tab.id, label: tab.label }))}
        value={active.id}
        onChange={(id) => {
          const tab = TABS.find((t) => t.id === id);
          if (tab) navigate(pathFor(tab));
        }}
        ariaLabel="Change Assurance Governance views"
      />

      <Outlet />
    </PageShell>
  );
};
